import {
  Article, Object as Objekt,
  Account, AccountType,
  Branch,
  DocumentRevisionsGroup, DocumentVersionInput,
  ConditionType,
  Configuration, ConfigurationAssignment, ConfigurationSection,
  ConfigurationVariable, ConfigurationVariablePayload, ConfigurationVariableValue,
  ContactPerson,
  CustomLine,
  CustomPropertyInputArbonia,
  HeaderLine,
  OfferSettingsUpdateInput, OfferTemplateSettingsUpdateInput,
  Partner,
  ProductData, ProductLine,
  Property,
  Quote,
  RemovedAssignment,
  SalesArea,
  VariableAssignmentPayload
} from 'graphql/models/generated';
import {
  LineSubtypePropVal
} from 'models/position.model';
import {
  CustomPricing, OfferPricing, ProductPricing
} from 'models/pricing.model';
import { FormsFieldsState } from 'models/layout-mod.model'

export type NonConfigurableProductProps = {
    width?: number;
    height?: number;
    thickness?: number;
    materialThickness?: number; // Dicke
    direction?: string; // Stärke
    fullDimensions?: string;
    ZUSATZ8?: string;
    GKNZ_1?: string;
} // used for lose, meine

export type AppArticle = Article & {
  isConfigurable?: boolean;
  customType?: LineSubtypePropVal;
  selected?: boolean;
  customDescription?: string;
  properties?: any[];
  nonConfigurableProductProps?: NonConfigurableProductProps
  customName?: string;
}

export interface SnkDirection {
  value?: string;
  element?: string;
}

export interface SnkCurrentDimensionType {
  value?: string;
}

export interface SnkDimension {
  width?: string;
  height?: string;
  thickness?: string;
  full?: string;
  direction?: string
}

export interface SnkDimensions {
  door?: SnkDimension;
  door2?: SnkDimension;
  frame?: SnkDimension;
  frame2?: SnkDimension;
  frame3?: SnkDimension;
  panel?: SnkDimension;
  direction?: SnkDirection;
  zfm?: SnkDimension;
  bram?: SnkDimension;
  bam?: SnkDimension;
  woem?: SnkDimension;
  currentType?: SnkCurrentDimensionType;
}

export type AppCustomLine = CustomLine & {
  article?: AppArticle | null;
  descriptiveText?: string;
  optionalOrAlternative?: boolean;
  positionNumber?: string;
  pricing?: CustomPricing;
  selected?: boolean;
  subLines?: ProductLine[];
  lineSubtype?: string;
}

export type AppLooseArticle = ProductData & {
  hasMore?: boolean;
  id?: string;
  externalImage?: string;
}

export interface ProductAssignment {
  index?: number;
  id: string; // variable.id
  varName?: string;
  name?: string; // value.name
  text?: string;
  value?: string; // value.value
  noPrintout?: boolean;
}

export type SnkProduct = ProductData & {
  __typename?: string;
  customType?: LineSubtypePropVal;
  name: string;
  description: string;
  customDescription?: string;
  productName: string;
  isConfigurable: boolean;
  assignments?: ProductAssignment[];
  productAssignments: ProductAssignment[];
  positionAssignments: ProductAssignment[];
  selected?: boolean;
  properties?: any[];
  nonConfigurableProductProps?: NonConfigurableProductProps
}

export type AppHeaderLine = HeaderLine & {
  positionNumber?: string;
  descriptiveText?: string;
}

export type AppConfigurationVariable = ConfigurationVariable & {
  assigned?: ConfigurationAssignment;
  assignedValues: ConfigurationVariableValue[];
  calculatedValue?: Property;
  clearable?: boolean;
  disabled: boolean;
  disabledProp: Property;
  isChanged?: boolean;
  isNew?: boolean;
  layoutMod?: string[];
  noPrintout?: Property;
  required: boolean;
  requiredProp: Property;
  show: boolean;
  showProp: Property;
  showAs: string;
  showAsProp: Property;
  showInConfiguration?: Property;
  showFilter?: Property;
  state?: FormsFieldsState | string;
  text: string;
  validationRequired?: boolean;
  value: string | number | undefined;
  valueName?: string;
}

export type AppConfigurationVariablePayload = ConfigurationVariablePayload & {
  items: AppConfigurationVariable[];
}

export type AppConfigurationVariableValue = ConfigurationVariableValue & {
  show?: {
    valueText?: string;
  }
  noPrintout?: {
    valueText?: string;
  }
}

export type AppConfigurationSection = ConfigurationSection & {
  variables?: AppConfigurationVariablePayload;
  show?: boolean;
  showProp?: Property | null;
  showFilterProp?: Property | null;
  isNew?: boolean;
  isChanged?: boolean;
  sections?: AppConfigurationSection[];
}

export type AppConfiguration = Configuration & {
  sections?: AppConfigurationSection[];
  sectionFilters?: AppConfigurationSection;
}

export interface GlassPanel {
  id: string;
  hoehe?: string;
  breite?: string;
}

export type AppProductLine = ProductLine & {
  configuration?: AppConfiguration;
  descriptiveText?: string;
  isOptional?: boolean;
  optionalOrAlternative?: boolean;
  positionNumber?: string;
  pricing?: ProductPricing;
  product: SnkProduct;
  snkProductName?: string;
  snkProductText?: string;
  snkDimensions?: SnkDimensions;
  selected?: boolean;
  subLines?: ProductLine[];
  lineSubtype?: string;
  glassPanels?: GlassPanel[];
}

export type AppRemovedAssignment = RemovedAssignment & {
  exclude?: boolean;
}

// *** not available in generated.ts
export enum RevisionsStatus {
  ALL_ACTIVE = 'All',
  NEW = 'New',
  IN_PROGRESS = 'InProgress',
  PENDING_RFP = 'Pending',
  ANSWERED_RFP = 'Answered',
  CLOSED = 'Closed',
  SENT = 'Sent',
}

// *** not available in generated.ts
export type SnkModifier = Account & {
  name: string;
}

export type AppQuote = Omit<Quote, 'lines'> & {
  id?: string;
  line?: AppProductLine;
  documentId: string;
  title?: string;
  lines?: (AppCustomLine | AppProductLine)[];
  linesCount?: number;
  locked: boolean;
  revision: number;
  totalNetValue?: number;
  commission?: {
    stringValue: string;
  };
  // customer?: Customer | null;
  contactPerson?: ContactPerson | null;
  object?: Objekt | null;
  textTemplateId?: {
    stringValue: string;
  };
  textTemplatePreText?: {
    stringValue: string;
  };
  textTemplatePostText?: {
    stringValue: string;
  };
  dealerOfferTitle?: {
    stringValue: string;
  };
  manufacturerOfferTitle?: {
    stringValue: string;
  };
  dealerObjectId?: {
    stringValue: string;
  };
  manufacturerObjectId?: {
    stringValue: string;
  };
  branchId?: {
    stringValue: string;
  };
  sendMessage?: {
    stringValue: string;
  };
  calculationType?: {
    stringValue: string;
  };
  isTempOffer?: {
    booleanValue: boolean;
  };
  createdFromRevision?: {
    integerValue: number;
  };
  pricing?: OfferPricing;
  createdDate?: string;
  modifiedDate?: string;
  validTo?: string;
  status?: RevisionsStatus;
  salesArea?: SalesArea;
  createdByType: AccountType;
  snkCreator: Account;
  snkModifier: SnkModifier;
  snkReceiver?: Account;
  branch?: Branch;
  partner?: Partner;
}

// *** not available in generated.ts
export enum LineCustomPropertyKeyType {
  DescriptiveText = 'DescriptiveText',
  ProductID = 'ProductID',
  ArticleID = 'ArticleID',
  LineID = 'LineID',
  Optional = 'Optional',
  ConfigurationSnapshot = 'ConfigurationSnapshot',
  OptionalOrAlternative = 'OptionalOrAlternative',
  LineSubtype = 'LineSubtype',
  CustomPositionskurztext = 'CustomPositionskurztext',
  FreieArtikelDescription = 'FreieArtikelDescription',
  CurrentDimensionChains = 'CurrentDimensionChains',
  MeineArtikelDescription = 'FreieArtikelDescription',
  CustomConfigurationSnapshot = 'CustomConfigurationSnapshot'
}

// *** not available in generated.ts
export enum QuoteCustomPropertyKeyType {
  CalculationType = 'CalculationType',
  CustomerID = 'CustomerID',
  ContactPersonID = 'ContactPersonID',
  ObjectID = 'ObjectID',
  DealerObjectID = 'DealerObjectID',
  ManufacturerObjectID = 'ManufacturerObjectID',
  TextTemplateId = 'TextTemplateId',
  TextTemplatePreText = 'TextTemplate1',
  MinicartReferences = 'References',
  TextTemplatePostText = 'TextTemplate2',
  SendSubject = 'SendSubject',
  SendUserId = 'SendUserId',
  SendMessage = 'SendMessage',
  BranchID = 'BranchID',
  IsTempOffer = 'IsTempConfig',
  Commission = 'Commission'
}

// *** not available in generated.ts
export enum ProductDataPropertyNameType {
  Description = 'Beschreibung'
}

export type AppVariableAssignmentPayload = Omit<VariableAssignmentPayload, 'line' | 'Quote'> & {
  line: AppProductLine;
  quote: AppQuote;
}

export type AppCustomPropertyInputArbonia = CustomPropertyInputArbonia & {
  key: LineCustomPropertyKeyType | QuoteCustomPropertyKeyType;
}

// *** not available in generated.ts
export type RevisionsGroup = DocumentRevisionsGroup & {
  latestRevisionNumber?: number;
}

/* ************** from input.model */
export interface CreateOneInput<C> {
  create?: C;
}

export interface CreateManyInput<C> {
  create?: C[];
}

export interface UpdateOneInput<C, U> {
  create?: C;
  update?: U;
}

export interface UpdateManyInput<C, U> {
  create?: C[];
  update?: {
    where: { id: string };
    data: U;
  }[];
}

export interface BranchOfferSettingsUpdateInput {
  offerSettings: {
    update?: OfferSettingsUpdateInput;
    upsert?: any;
  };
}

export interface BranchOfferTemplateSettingsUpdateInput {
  offerTemplateSettings: {
    update: OfferTemplateSettingsUpdateInput;
  };
}

/* ************** from quote.model */

export interface CustomProperty {
  stringValue: string;
  key: LineCustomPropertyKeyType | QuoteCustomPropertyKeyType;
}

// *** not available in generated.ts
export interface GroupedConfigurationVariable extends Partial<AppConfigurationVariable> {
  itemGroup?: AppConfigurationVariable[];
}

// *** not available in generated.ts
export interface OfferProductPosition<T extends AppProductLine | AppCustomLine = AppProductLine | AppCustomLine> {
  article?: any;
  product: SnkProduct | AppArticle;
  positions: Array<T>;
}

// *** not available in generated.ts
export interface PositionPricingUpdate {
  [lineId: string]: ProductPricing | CustomPricing;
}

// *** not available in generated.ts
export interface Offer extends Omit<AppQuote,
  'textTemplateId' |
  'textTemplatePreText' |
  'textTemplatePostText' |
  'dealerOfferTitle' |
  'manufacturerOfferTitle' |
  'snkModifier' |
  'sendMessage' |
  'calculationType' |
  'isTempOffer' |
  'createdFromRevision' |
  'branchId' |
  'autoUpdatePrices' |
  'priceIsOutdated'
> {
  productPositions: OfferProductPosition[];
  textTemplateId: string;
  textTemplatePreText: string;
  textTemplatePostText: string;
  dealerOfferTitle: string;
  manufacturerOfferTitle: string;
  branchId: string;
  snkVersion: string;
  snkModifier: SnkModifier;
  sendMessage: string;
  calculationType: ConditionType;
  isTempOffer?: boolean;
  createdFromRevision: number;
  positionNumbers?: string[];
  positionPricingUpdate?: PositionPricingUpdate;
  offerPricingUpdate?: OfferPricing;
  updatePositionPricingForms?: boolean;
  customProductPositionUpdate?: OfferProductPosition;
  productPositionsUpdate?: OfferProductPosition[];
  oldObjectLogic?: boolean;
  autoUpdatePrices?: boolean;
  priceIsOutdated?: boolean;
}

// *** not available in generated.ts
export interface SnkLineIdValue {
  Parent: 'SnkMain';
}

// *** not available in generated.ts
export const SnkLineId: SnkLineIdValue = {
  Parent: 'SnkMain'
};

// *** not available in generated.ts
export enum ConfigurationVariableType {
  NUMBER = 'NUMBER',
  STRING = 'STRING',
  DATE = 'DATE',
  BOOLEAN = 'BOOLEAN'
}

export enum ConfigurationVariableValueType {
  ANY_VALUE = 'ANY_VALUE',
  INTERVAL_VALUE = 'INTERVAL_VALUE',
  SINGLETON_VALUE = 'SINGLETON_VALUE'
}

export enum ConfigurationBooleanValue {
  TRUE = 'True',
  FALSE = 'False',
}
export enum SortOrderValueKey {
  ASC = 'asc',
  DESC = 'desc'
}

export enum OrderValueKey {
  ASC = 'Ascending',
  DESC = 'Descending'
}

export interface AssignmentChange {
  changedPositions: AppProductLine[];
  removedVariables: Pick<AppConfigurationVariable, 'id' | 'name'>[];
  updatedPosition?: AppProductLine;
  pricing?: ProductPricing;
}

export enum AdvancedDimensionTypes {
  ZFM = 'ZFM',
  BRAM = 'BRAM',
  BAM = 'BAM',
  WOEM = 'WOEM'
}

export interface ProductPositionsAssignmentsQueryVars {
  documentVersion: DocumentVersionInput,
  lineIds?: string[],
  parentLineId?: string,
  hideProductValues?: boolean,
  hidePositionValues?: boolean,
  hideValues?: boolean
}
